import { isJotformApiUrl } from 'src/utils/security/jotform-privacy';
import {
  isSensitiveDataKey,
  redactSensitiveData,
  isPaymentLinkApiUrl,
} from 'src/utils/security/redact-sensitive-data';

import { useSystemStore } from 'src/store/SystemStore';

import {
  readPersistedApiCallHistory,
  writePersistedApiCallHistory,
  deletePersistedApiCallHistory,
} from './api-call-history-persistence';

export const API_CALL_HISTORY_LIMIT = 100;
export const API_CALL_BODY_CHARACTER_LIMIT = 32_000;

const REDACTED = '[REDACTED]';

export type ApiCallStatus =
  | 'PENDING'
  | 'SUCCEEDED'
  | 'HTTP_ERROR'
  | 'NETWORK_ERROR'
  | 'CANCELED'
  | 'INTERRUPTED';

export type ApiCallRequestBody = {
  kind: 'empty' | 'json' | 'text' | 'form-data' | 'binary' | 'redacted';
  value: unknown;
  truncated: boolean;
  originalCharacterCount: number | null;
};

export type ApiCallRecord = {
  id: string;
  method: string;
  url: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  status: ApiCallStatus;
  httpStatus: number | null;
  requestBody: ApiCallRequestBody;
  errorCode: string | null;
  errorMessage: string | null;
};

export type BeginApiCallInput = {
  method?: string;
  url: string;
  body?: unknown;
  sensitiveRequest?: boolean;
};

export type CompleteApiCallInput = {
  status: Exclude<ApiCallStatus, 'PENDING'>;
  httpStatus?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
};

let history: readonly ApiCallRecord[] = [];
let sequence = 0;
const listeners = new Set<() => void>();
let hydrationPromise: Promise<void> | null = null;
let hydrationComplete = false;
let ignorePersistedHistory = false;
let persistenceRequested = false;
let persistenceIntent: 'write' | 'delete' = 'write';
let persistenceWorker: Promise<void> | null = null;

const API_CALL_STATUSES: ReadonlySet<ApiCallStatus> = new Set<ApiCallStatus>([
  'PENDING',
  'SUCCEEDED',
  'HTTP_ERROR',
  'NETWORK_ERROR',
  'CANCELED',
  'INTERRUPTED',
]);
const API_CALL_BODY_KINDS: ReadonlySet<ApiCallRequestBody['kind']> = new Set<
  ApiCallRequestBody['kind']
>(['empty', 'json', 'text', 'form-data', 'binary', 'redacted']);

function emitChange(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A diagnostic subscriber must not affect request execution.
    }
  });
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNumberOrNull(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

function isPersistedApiCallRecord(value: unknown): value is ApiCallRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<ApiCallRecord>;
  const requestBody = record.requestBody as Partial<ApiCallRequestBody> | undefined;
  return (
    typeof record.id === 'string' &&
    typeof record.method === 'string' &&
    typeof record.url === 'string' &&
    typeof record.startedAt === 'string' &&
    Number.isFinite(Date.parse(record.startedAt)) &&
    isStringOrNull(record.completedAt) &&
    isNumberOrNull(record.durationMs) &&
    typeof record.status === 'string' &&
    API_CALL_STATUSES.has(record.status as ApiCallStatus) &&
    isNumberOrNull(record.httpStatus) &&
    Boolean(requestBody) &&
    typeof requestBody?.kind === 'string' &&
    API_CALL_BODY_KINDS.has(requestBody.kind as ApiCallRequestBody['kind']) &&
    typeof requestBody.truncated === 'boolean' &&
    isNumberOrNull(requestBody.originalCharacterCount) &&
    isStringOrNull(record.errorCode) &&
    isStringOrNull(record.errorMessage)
  );
}

function restoredRecord(record: ApiCallRecord): ApiCallRecord {
  if (record.status !== 'PENDING') return record;
  return {
    ...record,
    status: 'INTERRUPTED',
    errorCode: 'APP_RESTARTED',
    errorMessage: 'The app stopped before this request returned a response.',
  };
}

function persistedRecords(value: unknown): {
  records: ApiCallRecord[];
  restoredPending: boolean;
} {
  if (!value || typeof value !== 'object') return { records: [], restoredPending: false };
  const payload = value as { version?: unknown; records?: unknown };
  if (payload.version !== 1 || !Array.isArray(payload.records)) {
    return { records: [], restoredPending: false };
  }
  const records = payload.records.filter(isPersistedApiCallRecord);
  return {
    records: records.map(restoredRecord),
    restoredPending: records.some((record) => record.status === 'PENDING'),
  };
}

function mergeHistory(restored: readonly ApiCallRecord[]): readonly ApiCallRecord[] {
  const recordsById = new Map(restored.map((record) => [record.id, record]));
  history.forEach((record) => recordsById.set(record.id, record));
  return Array.from(recordsById.values())
    .sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt))
    .slice(0, API_CALL_HISTORY_LIMIT);
}

async function flushPersistence(): Promise<void> {
  if (persistenceWorker) return persistenceWorker;

  const persistNextSnapshot = async (): Promise<void> => {
    if (!persistenceRequested) return;
    persistenceRequested = false;
    const intent = persistenceIntent;
    const snapshot = history;
    try {
      if (intent === 'delete') await deletePersistedApiCallHistory();
      else await writePersistedApiCallHistory(snapshot);
    } catch {
      // Diagnostic persistence must never affect request execution.
    }
    await persistNextSnapshot();
  };

  persistenceWorker = (async () => {
    await initializeApiCallHistory();
    await persistNextSnapshot();
  })().finally(() => {
    persistenceWorker = null;
    if (persistenceRequested) flushPersistence().catch(() => undefined);
  });
  return persistenceWorker;
}

function requestPersistence(intent: 'write' | 'delete'): void {
  persistenceIntent = intent;
  persistenceRequested = true;
  flushPersistence().catch(() => undefined);
}

export function initializeApiCallHistory(): Promise<void> {
  if (hydrationPromise) return hydrationPromise;
  hydrationPromise = (async () => {
    try {
      const persisted = persistedRecords(await readPersistedApiCallHistory());
      if (!ignorePersistedHistory && persisted.records.length > 0) {
        history = mergeHistory(persisted.records);
        emitChange();
        if (persisted.restoredPending) requestPersistence('write');
      }
    } catch {
      // The in-memory recorder remains available when local persistence is unavailable.
    } finally {
      hydrationComplete = true;
    }
  })();
  return hydrationPromise;
}

export function isApiCallHistoryHydrated(): boolean {
  return hydrationComplete;
}

async function waitForPersistenceIdle(): Promise<void> {
  if (!persistenceRequested && !persistenceWorker) return;
  await flushPersistence();
  await waitForPersistenceIdle();
}

export async function flushApiCallHistoryPersistence(): Promise<void> {
  await initializeApiCallHistory();
  await waitForPersistenceIdle();
}

function currentOrigin(): string {
  return typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
}

function resolveUrl(url: string, baseUrl?: string): URL | null {
  try {
    return new URL(url, baseUrl || currentOrigin());
  } catch {
    return null;
  }
}

/**
 * Paths that must never be recorded. The diagnostic log upload carries the
 * device's own captured records; mirroring it back into the history (and from
 * there into the next capture) is an unbounded feedback loop.
 */
const EXCLUDED_HISTORY_PATHS = ['/api/device/logs'];

/** Only PointCraft-owned HTTP origins are eligible for the admin history. */
export function isPointCraftApiUrl(url: string, requestBaseUrl?: string): boolean {
  const requestUrl = resolveUrl(url, requestBaseUrl);
  if (!requestUrl) return false;
  if (EXCLUDED_HISTORY_PATHS.some((path) => requestUrl.pathname.startsWith(path))) return false;

  const { baseUrl, baseAthUrl, basePublicUrl } = useSystemStore.getState();
  return [baseUrl, baseAthUrl, basePublicUrl]
    .map((candidate) => resolveUrl(candidate))
    .some((candidate) => candidate?.origin === requestUrl.origin);
}

function sanitizeUrl(url: string, requestBaseUrl?: string): string {
  const resolved = resolveUrl(url, requestBaseUrl);
  if (!resolved) return redactSensitiveData(url);

  resolved.searchParams.forEach((_value, key) => {
    if (isSensitiveDataKey(key)) resolved.searchParams.set(key, REDACTED);
  });
  return redactSensitiveData(resolved.toString());
}

function isFile(value: unknown): value is File {
  return typeof File !== 'undefined' && value instanceof File;
}

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function isUrlSearchParams(value: unknown): value is URLSearchParams {
  return typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams;
}

function isArrayBuffer(value: unknown): value is ArrayBuffer {
  return typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer;
}

function isArrayBufferView(value: unknown): value is ArrayBufferView {
  return typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value);
}

function makeSerializable(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0
): unknown {
  if (value === null || value === undefined) return value ?? null;
  if (typeof value === 'string') return redactSensitiveData(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'symbol' || typeof value === 'function') return `[${typeof value}]`;
  if (depth >= 12) return '[Maximum depth reached]';

  if (isFile(value)) {
    return { type: 'File', name: value.name, size: value.size, mimeType: value.type || null };
  }
  if (isBlob(value)) {
    return { type: 'Blob', size: value.size, mimeType: value.type || null };
  }
  if (isArrayBuffer(value)) return { type: 'ArrayBuffer', byteLength: value.byteLength };
  if (isArrayBufferView(value)) {
    return { type: value.constructor.name, byteLength: value.byteLength };
  }
  if (value instanceof Date) return value.toISOString();

  if (typeof value !== 'object') return String(value);
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  if (isFormData(value)) {
    const entries: Record<string, unknown> = {};
    value.forEach((entry, key) => {
      const item = isSensitiveDataKey(key) ? REDACTED : makeSerializable(entry, seen, depth + 1);
      const existing = entries[key];
      entries[key] =
        existing === undefined
          ? item
          : [...(Array.isArray(existing) ? existing : [existing]), item];
    });
    return entries;
  }

  if (isUrlSearchParams(value)) {
    const entries: Record<string, unknown> = {};
    value.forEach((entry, key) => {
      entries[key] = isSensitiveDataKey(key) ? REDACTED : redactSensitiveData(entry);
    });
    return entries;
  }

  if (Array.isArray(value)) {
    return value.map((item) => makeSerializable(item, seen, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      isSensitiveDataKey(key) ? REDACTED : makeSerializable(item, seen, depth + 1),
    ])
  );
}

function inferBodyKind(body: unknown): ApiCallRequestBody['kind'] {
  if (body === null || body === undefined) return 'empty';
  if (isFormData(body)) return 'form-data';
  if (isFile(body) || isBlob(body) || isArrayBuffer(body) || isArrayBufferView(body)) {
    return 'binary';
  }
  if (typeof body === 'string') return 'text';
  return 'json';
}

function parseJsonString(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
}

export function sanitizeApiRequestBody(
  body: unknown,
  sensitiveRequest = false
): ApiCallRequestBody {
  if (sensitiveRequest) {
    return {
      kind: 'redacted',
      value: '[REDACTED: sensitive request]',
      truncated: false,
      originalCharacterCount: null,
    };
  }
  if (body === null || body === undefined) {
    return { kind: 'empty', value: null, truncated: false, originalCharacterCount: 0 };
  }

  try {
    const normalized = makeSerializable(typeof body === 'string' ? parseJsonString(body) : body);
    const serialized = typeof normalized === 'string' ? normalized : JSON.stringify(normalized);
    if (serialized.length <= API_CALL_BODY_CHARACTER_LIMIT) {
      return {
        kind: inferBodyKind(body),
        value: normalized,
        truncated: false,
        originalCharacterCount: serialized.length,
      };
    }

    return {
      kind: inferBodyKind(body),
      value: `${serialized.slice(0, API_CALL_BODY_CHARACTER_LIMIT)}…`,
      truncated: true,
      originalCharacterCount: serialized.length,
    };
  } catch {
    return {
      kind: 'redacted',
      value: '[Unavailable: request body could not be safely serialized]',
      truncated: false,
      originalCharacterCount: null,
    };
  }
}

function nextId(): string {
  sequence += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${sequence}`;
}

export function beginApiCall(input: BeginApiCallInput, requestBaseUrl?: string): string | null {
  try {
    if (isJotformApiUrl(input.url) || isPaymentLinkApiUrl(input.url)) return null;
    initializeApiCallHistory().catch(() => undefined);
    if (!useSystemStore.getState().apiCallCaptureEnabled) return null;
    if (!isPointCraftApiUrl(input.url, requestBaseUrl)) return null;

    const id = nextId();
    const record: ApiCallRecord = {
      id,
      method: (input.method || 'GET').toUpperCase(),
      url: sanitizeUrl(input.url, requestBaseUrl),
      startedAt: new Date().toISOString(),
      completedAt: null,
      durationMs: null,
      status: 'PENDING',
      httpStatus: null,
      requestBody: sanitizeApiRequestBody(input.body, input.sensitiveRequest),
      errorCode: null,
      errorMessage: null,
    };
    history = [record, ...history].slice(0, API_CALL_HISTORY_LIMIT);
    emitChange();
    requestPersistence('write');
    return id;
  } catch {
    // Diagnostic capture must never block or change the real request.
    return null;
  }
}

export function completeApiCall(id: string | null | undefined, result: CompleteApiCallInput): void {
  if (!id) return;
  let changed = false;
  const completedAt = new Date();
  history = history.map((record) => {
    if (record.id !== id || record.status !== 'PENDING') return record;
    changed = true;
    return {
      ...record,
      completedAt: completedAt.toISOString(),
      durationMs: Math.max(0, completedAt.getTime() - Date.parse(record.startedAt)),
      status: result.status,
      httpStatus: result.httpStatus ?? null,
      errorCode: result.errorCode ? redactSensitiveData(result.errorCode).slice(0, 200) : null,
      errorMessage: result.errorMessage
        ? redactSensitiveData(result.errorMessage).slice(0, 1_000)
        : null,
    };
  });
  if (changed) {
    emitChange();
    requestPersistence('write');
  }
}

export function getApiCallHistorySnapshot(): readonly ApiCallRecord[] {
  return history;
}

export function subscribeApiCallHistory(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearApiCallHistory(): void {
  ignorePersistedHistory = true;
  history = [];
  emitChange();
  requestPersistence('delete');
}
